import axios from "axios";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const Steps1 = ({
  count,
  setCount,
  detailsSet,
  details,
  initialData,
  completeCurrentStep,
}) => {
  const [showOtpField, setShowOtpField] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const initialErrorData = {
    mobileNumber: "",
    otp: "",
  };
  const [errors, setErrors] = useState(initialErrorData);

  useEffect(() => {
    if (details) {
      setFormData(details);
      // Restore OTP verification state if details exist
      if (details.otp && details.mobileNumber) {
        setShowOtpField(true);
        setOtpVerified(true);
      }
    }
  }, [details]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error when user types
    if (value.trim() !== "") {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateMobile = () => {
    const mobile = formData?.mobileNumber?.trim();
    if (!mobile) {
      setErrors((prev) => ({
        ...prev,
        mobileNumber: "Please enter Mobile Number",
      }));
      return false;
    }
    if (!/^\d{10}$/.test(mobile)) {
      setErrors((prev) => ({
        ...prev,
        mobileNumber: "Mobile number must be 10 digits",
      }));
      return false;
    }
    return true;
  };

  const validateOtp = () => {
    const otp = formData?.otp?.trim();
    if (!otp) {
      setErrors((prev) => ({ ...prev, otp: "Please enter OTP" }));
      return false;
    }
    if (!/^\d{4}$/.test(otp)) {
      setErrors((prev) => ({ ...prev, otp: "OTP must be 4 digits" }));
      return false;
    }
    return true;
  };

  const sendOtp = async () => {
    if (!validateMobile()) return;

    try {
      setIsSubmitting(true);
      const response = await axios.post(
        `https://anoopam.com/aims/api/?action=otp&mobile=${formData.mobileNumber}`
      );

      if (response?.data?.status === "success") {
        Swal.fire({ text: response.data.msg, icon: "success" });
        setShowOtpField(true);
        setOtpVerified(false);
      } else {
        Swal.fire({
          text: response?.data?.message || "Failed to send OTP",
          icon: "error",
        });
      }
    } catch (error) {
      Swal.fire({
        text: "Failed to send OTP. Please try again.",
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const verifyOtp = async () => {
    if (!validateMobile() || !validateOtp()) return;

    try {
      setIsSubmitting(true);
      const response = await axios.post(
        `https://anoopam.com/aims/api/?action=verifyotp&mobile=${formData.mobileNumber}&otp=${formData.otp}`
      );

      if (response?.data?.status === "success") {
        Swal.fire({ text: response.data.msg, icon: "success" });
        setOtpVerified(true);
        localStorage.setItem("aimsUserToken", response.data.token);
        localStorage.setItem("aimsUserMemberId", response.data.data?.memberid);

        // Update parent component with verified data
        const updatedData = { ...formData, verified: true };
        detailsSet(updatedData);

        // Move to next step if this is step 1
        if (count === 1) {
          setCount(count + 1);
          completeCurrentStep(count);
        }
      } else {
        Swal.fire({
          text: response?.data?.message || "Invalid OTP",
          icon: "error",
        });
      }
    } catch (error) {
      Swal.fire({
        text: "Failed to verify OTP. Please try again.",
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!otpVerified) {
      Swal.fire({ text: "Please verify your OTP first", icon: "warning" });
      return;
    }

    // For steps beyond the first one
    detailsSet(formData);
    setCount(count + 1);
    completeCurrentStep(count);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mt-4 main p-3 p-md-4 border rounded-4 bg-white">
        <div className="row">
          <div className="col-12 col-md-8 col-lg-6 col-xl-5">
            <label className="tittle-text" htmlFor="mobileNumber">
              Mobile Number
            </label>
            <div className="d-flex flex-column flex-md-row gap-2 gap-md-3">
              <div className="flex-grow-1">
                <input
                  type="tel" // Changed from number to tel for better mobile UX
                  id="mobileNumber"
                  name="mobileNumber"
                  value={formData?.mobileNumber || ""}
                  onChange={handleInputChange}
                  placeholder="Enter Mobile Number"
                  className="form-control mt-2"
                  maxLength="10"
                  disabled={otpVerified}
                />
                {errors?.mobileNumber && (
                  <span className="text-danger fw-medium mt-1 d-inline-block">
                    {errors?.mobileNumber}
                  </span>
                )}
              </div>
              <div className="mt-2 mt-md-0">
                <button
                  type="button"
                  style={{
                    width: "100%",
                    minWidth: "120px",
                    maxWidth: "180px",
                    height: "50px",
                    borderRadius: "40px",
                    background: "#034da2",
                    color: "white",
                    border: "none",
                    fontWeight: 600,
                    fontSize: "1rem",
                  }}
                  className="px-2 px-md-4"
                  onClick={sendOtp}
                  disabled={isSubmitting || otpVerified}
                >
                  {isSubmitting ? "Sending..." : "Send OTP"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {showOtpField && !otpVerified && (
          <div className="row mt-3">
            <div className="col-12 col-md-8 col-lg-6 col-xl-5">
              <label className="tittle-text">Enter OTP</label>
              <div className="d-flex flex-column flex-md-row gap-2 gap-md-3">
                <div className="flex-grow-1">
                  <input
                    type="tel" // Changed from number to tel
                    name="otp"
                    value={formData?.otp || ""}
                    onChange={handleInputChange}
                    placeholder="4 Digit OTP"
                    className="form-control mt-2"
                    maxLength="4"
                  />
                  {errors?.otp && (
                    <span className="text-danger fw-medium mt-1 d-inline-block">
                      {errors?.otp}
                    </span>
                  )}
                </div>
                <div className="mt-2 mt-md-0">
                  <button
                    type="button"
                    style={{
                      width: "100%",
                      minWidth: "120px",
                      maxWidth: "180px",
                      height: "50px",
                      borderRadius: "40px",
                      background: "#034da2",
                      color: "white",
                      border: "none",
                      fontWeight: 600,
                      fontSize: "1rem",
                    }}
                    className="px-2 px-md-4"
                    onClick={verifyOtp}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Verifying..." : "Verify OTP"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {otpVerified && (
          <div className="row mt-3">
            <div className="col-12">
              <div className="alert alert-success">
                Mobile number verified successfully!
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="d-flex justify-content-end gap-3 mt-3">
        {count !== 0 && (
          <button
            type="button"
            className="rounded-5 btnBlue"
            onClick={() => navigate("/registration")}
          >
            Previous
          </button>
        )}
        {otpVerified && count < 5 && (
          <button
            className="rounded-5 btnBlue"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Processing..." : "Next"}
          </button>
        )}
      </div>
    </form>
  );
};

export default Steps1;
